// Pure functions for transforming assistant message markdown for copy/export.
// No React imports — safe to use from anywhere.

import type { SourceDoc } from '../types';

export type CopyTextFormat = 'markdown' | 'plain' | 'latex' | 'tsv';

export type CopyRefFormat =
  | 'wiki'
  | 'malpedia'
  | 'url'
  | 'both'
  | 'numbered-plain'
  | 'strip';

export type CopyTableColumn = 'cite_key' | 'title' | 'url' | 'score';

export type CopyScope =
  | { kind: 'full' }
  | { kind: 'table'; index: number }
  | { kind: 'sources' }
  | { kind: 'selection'; text: string };

export interface CopyChoices {
  scope: CopyScope;
  refFormat: CopyRefFormat;
  format: CopyTextFormat;
  includeSourceTable: boolean;
}

const DEFAULT_TABLE_COLUMNS: CopyTableColumn[] = ['cite_key', 'title', 'url'];

/** Assign sequential numbers to cite_keys in order of first appearance in body. */
export function buildCiteNumberMap(body: string): Map<string, number> {
  const map = new Map<string, number>();
  const regex = /\[\[([^\]]+)\]\]/g;
  let match: RegExpExecArray | null;
  let counter = 1;
  while ((match = regex.exec(body)) !== null) {
    if (!map.has(match[1])) map.set(match[1], counter++);
  }
  return map;
}

function malpediaPath(citeKey: string): string {
  return `/sources/${encodeURIComponent(citeKey)}`;
}

/** Rewrite every `[[cite_key]]` token in body per `refFormat`. */
export function transformBody(
  body: string,
  sources: SourceDoc[] | undefined,
  refFormat: CopyRefFormat,
): string {
  const sourceMap = new Map((sources || []).map((s) => [s.cite_key, s]));
  const numberMap = buildCiteNumberMap(body);

  return body.replace(/\[\[([^\]]+)\]\]/g, (_, key: string) => {
    const num = numberMap.get(key) ?? '?';
    const src = sourceMap.get(key);
    const url = src?.url;
    const internal = malpediaPath(key);

    switch (refFormat) {
      case 'wiki':
        return `[[${key}]]`;
      case 'malpedia':
        return `[${num}](${internal})`;
      case 'url':
        return `[${num}](${url || internal})`;
      case 'both':
        return url
          ? `[${num}](${url}) ([${key}](${internal}))`
          : `[${num}](${internal})`;
      case 'numbered-plain':
        return `[${num}]`;
      case 'strip':
        return '';
      default:
        return `[[${key}]]`;
    }
  });
}

/** Build a markdown table summarising sources with the chosen columns. */
export function buildSourceTable(
  sources: SourceDoc[] | undefined,
  columns: CopyTableColumn[] = DEFAULT_TABLE_COLUMNS,
): string {
  if (!sources || sources.length === 0 || columns.length === 0) return '';

  const headers: Record<CopyTableColumn, string> = {
    cite_key: 'Cite Key',
    title: 'Title',
    url: 'URL',
    score: 'Score',
  };

  const lines: string[] = [];
  lines.push(`| ${columns.map((c) => headers[c]).join(' | ')} |`);
  lines.push(`| ${columns.map(() => '---').join(' | ')} |`);

  for (const s of sources) {
    const cells = columns.map((col) => {
      switch (col) {
        case 'cite_key':
          return s.cite_key;
        case 'title':
          return (s.title || '').replace(/\|/g, '\\|') || '-';
        case 'url':
          return s.url || '-';
        case 'score':
          return typeof s.score === 'number' ? s.score.toFixed(3) : '-';
        default:
          return '';
      }
    });
    lines.push(`| ${cells.join(' | ')} |`);
  }

  return lines.join('\n');
}

const TABLE_REGEX = /(?:^|\n)(\|[^\n]+\|\n\|[\s:|-]+\|\n(?:\|[^\n]*\|(?:\n|$))+)/g;

/**
 * Find the Nth pipe-style markdown table in `body` and return its raw markdown.
 * Tables are matched as a header row, a separator row, and one or more body rows.
 */
export function extractTableMarkdown(body: string, tableIndex: number): string | null {
  const regex = new RegExp(TABLE_REGEX.source, 'g');
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(body)) !== null) {
    if (i === tableIndex) return match[1].trim();
    i++;
  }
  return null;
}

/** Return every pipe-style markdown table slice in document order. */
export function findTables(body: string): string[] {
  const regex = new RegExp(TABLE_REGEX.source, 'g');
  const out: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    out.push(match[1].trim());
  }
  return out;
}

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  // Park escaped pipes before splitting on the column delimiter.
  s = s.replace(/\\\|/g, '\x02');
  return s.split('|').map((c) => c.trim().replace(/\x02/g, '|'));
}

const TSV_SEP_REGEX = /^\|[\s:|-]+\|$/;

/** Convert a pipe-style markdown table to tab-separated values. */
export function markdownTableToTsv(table: string): string {
  const lines = table.split('\n').filter((l) => l.trim().length > 0);
  const rows = lines.filter((l) => !TSV_SEP_REGEX.test(l.trim()));
  return rows.map((line) => splitTableRow(line).join('\t')).join('\n');
}

/** Escape LaTeX special characters in regular prose. Safe to call on any string. */
function escapeLatexProse(s: string): string {
  let out = s;
  // First park backslash/braces in markers so subsequent escapes don't double-escape.
  out = out.replace(/\\/g, '\x01BS\x01');
  out = out.replace(/\{/g, '\x01OB\x01');
  out = out.replace(/\}/g, '\x01CB\x01');
  out = out.replace(/&/g, '\\&');
  out = out.replace(/%/g, '\\%');
  out = out.replace(/_/g, '\\_');
  out = out.replace(/#/g, '\\#');
  out = out.replace(/\$/g, '\\$');
  out = out.replace(/~/g, '\\textasciitilde{}');
  out = out.replace(/\^/g, '\\textasciicircum{}');
  out = out.replace(/\x01BS\x01/g, '\\textbackslash{}');
  out = out.replace(/\x01OB\x01/g, '\\{');
  out = out.replace(/\x01CB\x01/g, '\\}');
  return out;
}

function markdownTableToLatex(table: string): string {
  const lines = table.split('\n').filter((l) => l.trim().length > 0);
  if (lines.length < 2) return '';
  const header = splitTableRow(lines[0]);
  const cols = header.length;
  const bodyLines = lines
    .slice(1)
    .filter((l) => !TSV_SEP_REGEX.test(l.trim()));
  const colSpec = `|${'l|'.repeat(cols)}`;
  const fmtCells = (cells: string[]): string => {
    const padded = cells.slice(0, cols);
    while (padded.length < cols) padded.push('');
    return padded.map((c) => escapeLatexProse(c)).join(' & ') + ' \\\\';
  };
  const rows: string[] = [fmtCells(header)];
  for (const line of bodyLines) rows.push(fmtCells(splitTableRow(line)));
  return `\\begin{tabular}{${colSpec}}\n\\hline\n${rows.join('\n\\hline\n')}\n\\hline\n\\end{tabular}`;
}

/**
 * Conservative regex-based markdown→LaTeX converter. Suitable for pasting into
 * a .tex file that uses `\usepackage{hyperref}` for `\href{}{}`.
 */
export function markdownToLatex(md: string): string {
  const tokens: string[] = [];
  const push = (s: string): string => {
    tokens.push(s);
    return `\x00LX${tokens.length - 1}\x00`;
  };

  let out = md;

  // 1. Fenced code blocks → verbatim (drop language tag).
  out = out.replace(/```[a-zA-Z0-9_-]*\n([\s\S]*?)```/g, (_, body: string) =>
    push(`\\begin{verbatim}\n${body.replace(/\n$/, '')}\n\\end{verbatim}`),
  );

  // 2. Inline code → \texttt{...}.
  out = out.replace(/`([^`]+)`/g, (_, code: string) => push(`\\texttt{${code}}`));

  // 3. Images → italic placeholder.
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, (_, alt: string) =>
    push(`\\textit{[image: ${escapeLatexProse(alt)}]}`),
  );

  // 4. Links → \href{url}{text}.
  out = out.replace(/\[([^\]]+)\]\(([^)]*)\)/g, (_, text: string, url: string) =>
    push(`\\href{${url}}{${escapeLatexProse(text)}}`),
  );

  // 5. Bold / italic (inline).
  out = out.replace(/\*\*([^*]+)\*\*/g, (_, t: string) =>
    push(`\\textbf{${escapeLatexProse(t)}}`),
  );
  out = out.replace(/\*([^*\n]+)\*/g, (_, t: string) =>
    push(`\\textit{${escapeLatexProse(t)}}`),
  );
  out = out.replace(/(^|[^A-Za-z0-9_])_([^_\n]+)_(?=$|[^A-Za-z0-9_])/g, (_, pre: string, t: string) =>
    `${pre}${push(`\\textit{${escapeLatexProse(t)}}`)}`,
  );

  // 6. Pipe-style tables (after inline so cell content may carry placeholders).
  out = out.replace(
    /(\|[^\n]+\|\n\|[\s:|-]+\|\n(?:\|[^\n]*\|(?:\n|$))+)/g,
    (table: string) => push(markdownTableToLatex(table)),
  );

  // 7. Headings.
  out = out.replace(/^### (.+)$/gm, (_, t: string) =>
    push(`\\subsubsection{${escapeLatexProse(t)}}`),
  );
  out = out.replace(/^## (.+)$/gm, (_, t: string) =>
    push(`\\subsection{${escapeLatexProse(t)}}`),
  );
  out = out.replace(/^# (.+)$/gm, (_, t: string) =>
    push(`\\section{${escapeLatexProse(t)}}`),
  );

  // 8. Block quotes — consecutive `> …` lines.
  out = out.replace(/(^>[^\n]*(?:\n>[^\n]*)*)/gm, (block: string) => {
    const inner = block.replace(/^> ?/gm, '');
    return push(`\\begin{quote}\n${escapeLatexProse(inner)}\n\\end{quote}`);
  });

  // 9. Bullet lists — consecutive `- …` lines.
  out = out.replace(/(^- [^\n]+(?:\n- [^\n]+)*)/gm, (block: string) => {
    const items = block
      .split('\n')
      .map((l) => l.replace(/^- /, ''))
      .map((item) => `  \\item ${escapeLatexProse(item)}`);
    return push(`\\begin{itemize}\n${items.join('\n')}\n\\end{itemize}`);
  });

  // 10. Escape remaining prose, skipping placeholder tokens.
  out = out
    .split(/(\x00LX\d+\x00)/)
    .map((chunk) =>
      chunk.startsWith('\x00LX') ? chunk : escapeLatexProse(chunk),
    )
    .join('');

  // 11. Substitute placeholders back.
  out = out.replace(/\x00LX(\d+)\x00/g, (_, i: string) => tokens[parseInt(i, 10)]);

  return out;
}

/** Strip common markdown syntax for plain-text targets. Conservative — keeps content readable. */
export function stripMarkdown(md: string): string {
  let out = md;
  out = out.replace(/```[a-zA-Z0-9_-]*\n([\s\S]*?)```/g, (_, body) => body);
  out = out.replace(/`([^`]+)`/g, '$1');
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  out = out.replace(/^#{1,6}\s+/gm, '');
  out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
  out = out.replace(/__([^_]+)__/g, '$1');
  out = out.replace(/\*([^*\n]+)\*/g, '$1');
  out = out.replace(/_([^_\n]+)_/g, '$1');
  out = out.replace(/^>\s?/gm, '');
  out = out.replace(/^(\s*)[-*+]\s+/gm, '$1• ');
  out = out.replace(/^\s*---+\s*$/gm, '');
  return out;
}

/**
 * Orchestrator: select a slice based on scope, transform refs in it, optionally
 * strip markdown, and (for full-message scope) optionally append a sources table.
 */
export function buildCopy(
  body: string,
  sources: SourceDoc[] | undefined,
  choices: CopyChoices,
): string {
  const { scope, refFormat, format, includeSourceTable } = choices;

  let slice = '';
  switch (scope.kind) {
    case 'full':
      slice = body;
      break;
    case 'table': {
      const t = extractTableMarkdown(body, scope.index);
      slice = t ?? '';
      break;
    }
    case 'sources':
      slice = buildSourceTable(sources, DEFAULT_TABLE_COLUMNS);
      break;
    case 'selection':
      slice = scope.text;
      break;
  }

  let out = transformBody(slice, sources, refFormat);
  if (format === 'plain') out = stripMarkdown(out);
  else if (format === 'tsv') out = markdownTableToTsv(out);
  else if (format === 'latex') out = markdownToLatex(out);

  if (
    scope.kind === 'full' &&
    includeSourceTable &&
    sources &&
    sources.length > 0
  ) {
    const table = buildSourceTable(sources, DEFAULT_TABLE_COLUMNS);
    if (table) {
      if (format === 'latex') {
        out = `${out.trimEnd()}\n\n\\section{Sources}\n${markdownToLatex(table)}\n`;
      } else if (format === 'plain') {
        out = `${out.trimEnd()}\n\nSources:\n${table}\n`;
      } else if (format !== 'tsv') {
        out = `${out.trimEnd()}\n\n## Sources\n\n${table}\n`;
      }
    }
  }
  return out;
}
